/* Country of a marketing page and the links that carry it.
 * Saudi Arabia keeps the root URLs ("/home", "/industries/garage"); the other GCC countries live under their slug
 * ("/ae", "/ae/industries", "/ae/industries/garage"), crawlable as "/ae/", "/ar/ae/industries/garage/" etc.
 * Sign-in and sign-up links carry the country (and the business category on industry pages) so the demo picker and
 * the signup form open on them: "/login?country=ae&demo=garage", "/setup?country=ae&category=garage". */
import { useLocation } from "react-router-dom";
import { COUNTRIES, countryBySlug, countryOf, type CountryInfo } from "../../domain/country";

/** Countries with their own URL prefix (everything but Saudi Arabia). */
export const PREFIXED = COUNTRIES.filter((c) => c.slug);

/** Router route → its country ("/ae/industries/garage" → UAE; anything else → Saudi Arabia). */
export function countryOfRoute(route: string): CountryInfo {
  const seg = route.split("?")[0].split("/")[1] ?? "";
  return (seg && PREFIXED.some((c) => c.slug === seg) ? countryBySlug(seg) : null) ?? countryOf("SA");
}

/** Country of the marketing page being shown. */
export function useMktCountry(): CountryInfo {
  return countryOfRoute(useLocation().pathname);
}

/** A marketing route of a country: "/home" → "/ae", "/industries/x" → "/ae/industries/x". Pricing, video and the
 * auth pages are shared by every country and stay unprefixed. */
export function inCountry(route: string, c: CountryInfo): string {
  if (!c.slug) return route;
  if (route === "/home") return "/" + c.slug;
  if (route === "/industries" || route.startsWith("/industries/")) return "/" + c.slug + route;
  return route;
}

/** The route without its country prefix ("/ae/industries/x" → "/industries/x", "/ae" → "/home"). */
export function stripCountry(route: string): string {
  const c = countryOfRoute(route);
  if (!c.slug) return route;
  const rest = route.slice(c.slug.length + 1);
  return rest === "" || rest === "/" ? "/home" : rest;
}

const qs = (p: [string, string | undefined][]) => {
  const s = p.filter(([, v]) => v).map(([k, v]) => k + "=" + encodeURIComponent(v!)).join("&");
  return s ? "?" + s : "";
};
/** Sign-in link from a marketing page: preselects the demo country and business category. */
export function loginRoute(c: CountryInfo, category?: string): string {
  return "/login" + qs([["country", c.slug || undefined], ["demo", category]]);
}
/** Sign-up link from a marketing page: preselects the country and business category. */
export function signupRoute(c: CountryInfo, category?: string): string {
  return "/setup" + qs([["country", c.slug || undefined], ["category", category]]);
}
